import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Play, Download, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useDatasetSchema } from '@/hooks/useDatasets';
import { useDatasetQuery } from '@/hooks/useDatasetQuery';
import { useSavedQueries, useCreateSavedQuery, useDeleteSavedQuery } from '@/hooks/useSavedQueries';
import { useDatasetExport } from '@/hooks/useDatasetExport';
import { QueryColumnPicker } from './QueryColumnPicker';
import { QueryFilterRow } from './QueryFilterRow';
import { QueryOrderRow } from './QueryOrderRow';
import { QueryResultsTable } from './QueryResultsTable';
import { encodeQueryState, decodeQueryState } from '../utils/queryUrlState';
import { rowsToCsv, resolveColumnOrder } from '../utils/exportCsv';
import { validateFilters } from '../lib/validateFilterValue';
import type { DatasetQueryRequest, DatasetExportRequest, QueryFilter, QueryOrderBy } from '../types/query.types';
import type { SavedQuery } from '../types/savedQuery.types';

export interface DatasetExploreProps { datasetId: string; }

export function DatasetExplore({ datasetId }: DatasetExploreProps) {
  const { data: schema, isLoading } = useDatasetSchema(datasetId);
  const columns = useMemo(
    () => (schema?.fields ?? []).map((f: any) => ({ name: f.name, type: f.type })),
    [schema],
  );
  const firstCol = columns[0]?.name ?? '';

  const [searchParams, setSearchParams] = useSearchParams();
  // Decode the initial ?q= exactly once (mount). Subsequent URL writes are ours.
  const initial = useMemo(() => decodeQueryState(searchParams.get('q')), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedColumns, setSelectedColumns] = useState<string[]>(initial?.columns ?? []);
  const [filters, setFilters] = useState<QueryFilter[]>(initial?.filters ?? []);
  const [orderBy, setOrderBy] = useState<QueryOrderBy[]>(initial?.orderBy ?? []);
  const limit = 50;

  const filterErrors = useMemo(() => validateFilters(filters, columns), [filters, columns]);
  const hasFilterErrors = filterErrors.some(Boolean);
  const [offset, setOffset] = useState(initial?.offset ?? 0);

  const query = useDatasetQuery(datasetId);
  const exportMutation = useDatasetExport(datasetId);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [exportFormat, setExportFormat] = useState<'csv' | 'parquet'>('csv');

  const { data: saved = [] } = useSavedQueries(datasetId);
  const createSaved = useCreateSavedQuery(datasetId);
  const deleteSaved = useDeleteSavedQuery(datasetId);

  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadSaved = (sq: SavedQuery) => {
    setSelectedColumns(sq.query_state.columns ?? []);
    setFilters(sq.query_state.filters ?? []);
    setOrderBy(sq.query_state.order_by ?? []);
    setOffset(0);
    // populate only — do NOT call run()
  };

  const saveQuery = async () => {
    const name = saveName.trim();
    if (!name) return;
    setSaveError(null);
    try {
      await createSaved.mutateAsync({
        name,
        query_state: { columns: selectedColumns, filters, order_by: orderBy },
      });
      setSaveName('');
    } catch (e) {
      setSaveError(((e as Error)?.message || '').trim() || 'Could not save query');
    }
  };

  const deleteSavedQuery = async (id: string) => {
    setDeleteError(null);
    try {
      await deleteSaved.mutateAsync(id);
    } catch (e) {
      setDeleteError(((e as Error)?.message || '').trim() || 'Could not delete query');
    }
  };

  const buildRequest = (off: number): DatasetQueryRequest => ({
    columns: selectedColumns.length ? selectedColumns : undefined,
    filters,
    order_by: orderBy,
    limit,
    offset: off,
    apply_masking: true,
  });

  const run = async (off: number) => {
    if (validateFilters(filters, columns).some(Boolean)) return; // don't run with invalid filters
    setOffset(off);
    // Sync the shareable URL (replace, not push) so the link reflects the run.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('q', encodeQueryState({ columns: selectedColumns, filters, orderBy, offset: off }));
        return next;
      },
      { replace: true },
    );
    await query.mutateAsync(buildRequest(off));
  };

  // Auto-run once on mount when arriving via a shared ?q= link. Deferred to a
  // microtask (not called synchronously in the effect body) since `run` itself
  // calls `setOffset` before its first `await` — same deferral precedent as
  // OAuthPopulatedDropdownField's auto-select effect. The ref guard flips
  // synchronously (safe: ref writes in effects are unrestricted) so a re-run of
  // this effect can never double-fire the deferred call.
  const didAutoRun = useRef(false);
  useEffect(() => {
    if (didAutoRun.current || isLoading) return; // wait for schema → columns populated before validating/auto-running
    didAutoRun.current = true;
    queueMicrotask(() => {
      if (initial) void run(initial.offset ?? 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const exportCsv = () => {
    const rows = query.data?.data ?? [];
    if (!rows.length) return;
    const order = resolveColumnOrder(rows, selectedColumns);
    const csv = rowsToCsv(rows, order);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dataset-${datasetId}-page${offset}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportAll = async () => {
    setExportError(undefined);
    const body: DatasetExportRequest = {
      columns: selectedColumns.length ? selectedColumns : undefined,
      filters,
      order_by: orderBy,
      apply_masking: true,
      format: exportFormat,
    };
    try {
      const resp = await exportMutation.mutateAsync(body);
      const blob = resp.data as Blob;
      const cd = (resp.headers?.['content-disposition'] as string) || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m?.[1] || `dataset-${datasetId}-export.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The apiClient interceptor decodes blob error bodies before throwing (#651),
      // so ApiError.message already carries the real backend text (e.g. the 413
      // over-cap message). Read it directly — no blob decoding needed here.
      setExportError(((err as Error)?.message || '').trim() || 'Export failed');
    }
  };

  // The apiClient normalizes all errors into an `ApiError extends Error` whose
  // `.message` is the human-readable backend message (it has NO `.response`).
  const errorDetail = query.error
    ? (((query.error as Error)?.message || '').trim() || 'Query failed')
    : undefined;

  // #680: a query 400 can carry per-field details:[{field,message}] when a filter value
  // is invalid for its column type (e.g. `inf` on an integer column — a value the client
  // validator accepts). Map field→message so the offending filter row shows it inline.
  const backendFieldErrors = useMemo<Record<string, string>>(() => {
    const err = query.error as { status?: number; details?: unknown } | undefined;
    if (!err || err.status !== 400 || !Array.isArray(err.details)) return {};
    const out: Record<string, string> = {};
    for (const d of err.details as Array<{ field?: unknown; message?: unknown }>) {
      if (typeof d?.field === 'string' && d.field && typeof d?.message === 'string') {
        out[d.field] = d.message;
      }
    }
    return out;
  }, [query.error]);

  // Suppress the destructive banner only when EVERY 400 detail maps to a current filter
  // column AND will actually render a non-empty inline message (errors are shown inline).
  // Non-400 errors, or 400s with unmapped/empty/missing-message details, still show the banner.
  const backendErrorAttributed = useMemo(() => {
    const err = query.error as { status?: number; details?: unknown } | undefined;
    if (!err || err.status !== 400 || !Array.isArray(err.details) || err.details.length === 0) {
      return false;
    }
    const cols = new Set(filters.map((f) => f.column));
    return (err.details as Array<{ field?: unknown }>).every(
      (d) => typeof d?.field === 'string' && cols.has(d.field) && Boolean(backendFieldErrors[d.field]),
    );
  }, [query.error, filters, backendFieldErrors]);
  const total = query.data?.metadata.total_rows ?? 0;
  const returned = query.data?.metadata.returned_rows ?? 0;
  const hasRows = (query.data?.data?.length ?? 0) > 0;

  if (isLoading) return <div className="p-4 text-muted-foreground">Loading schema…</div>;

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-4">
        <div>
          <div className="text-sm font-medium mb-2">Columns <span className="text-muted-foreground">(none = all)</span></div>
          <QueryColumnPicker columns={columns} selected={selectedColumns} onChange={setSelectedColumns} />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Filters</div>
          {filters.map((f, i) => (
            <QueryFilterRow key={i} filter={f} columns={columns}
              error={filterErrors[i] ?? backendFieldErrors[f.column] ?? undefined}
              onChange={(next) => setFilters(filters.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setFilters(filters.filter((_, j) => j !== i))} />
          ))}
          <Button variant="outline" size="sm" data-testid="add-filter"
            onClick={() => setFilters([...filters, { column: firstCol, operator: '=', value: '' }])}>
            <Plus className="h-4 w-4 mr-1" /> Add filter
          </Button>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Sort</div>
          {orderBy.map((o, i) => (
            <QueryOrderRow key={i} order={o} columns={columns}
              onChange={(next) => setOrderBy(orderBy.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setOrderBy(orderBy.filter((_, j) => j !== i))} />
          ))}
          <Button variant="outline" size="sm" data-testid="add-sort"
            onClick={() => setOrderBy([...orderBy, { column: firstCol, direction: 'asc' }])}>
            <Plus className="h-4 w-4 mr-1" /> Add sort
          </Button>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Saved queries</div>
          {saved.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {saved.map((sq) => (
                <div key={sq.id} className="flex items-center gap-1">
                  <button
                    data-testid="saved-query-item"
                    className="text-sm underline text-primary cursor-pointer"
                    onClick={() => loadSaved(sq)}
                  >
                    {sq.name}
                  </button>
                  {sq.is_owner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="saved-query-delete"
                      onClick={() => void deleteSavedQuery(sq.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              data-testid="save-name"
              className="border rounded px-2 py-1 text-sm flex-1"
              placeholder="Query name…"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
            />
            <Button
              data-testid="save-query"
              variant="outline"
              size="sm"
              disabled={!saveName.trim() || createSaved.isPending}
              onClick={saveQuery}
            >
              Save
            </Button>
          </div>
          {saveError && (
            <Alert variant="destructive"><AlertDescription>{saveError}</AlertDescription></Alert>
          )}
          {deleteError && (
            <Alert variant="destructive"><AlertDescription>{deleteError}</AlertDescription></Alert>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="run-query" disabled={query.isPending || hasFilterErrors} onClick={() => run(0)}>
            <Play className="h-4 w-4 mr-1" /> Run query
          </Button>
          {hasRows && (
            <Button variant="outline" data-testid="export-csv" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-1" /> Export page
            </Button>
          )}
          {hasRows && (
            <select
              data-testid="export-format"
              aria-label="Export format"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'parquet')}
              disabled={exportMutation.isPending}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="csv">CSV</option>
              <option value="parquet">Parquet</option>
            </select>
          )}
          {hasRows && (
            <Button
              variant="outline"
              data-testid="export-all"
              disabled={exportMutation.isPending}
              onClick={exportAll}
            >
              <Download className="h-4 w-4 mr-1" /> Export all
            </Button>
          )}
        </div>
      </Card>

      {errorDetail && !backendErrorAttributed && (
        <Alert variant="destructive"><AlertDescription>{String(errorDetail)}</AlertDescription></Alert>
      )}
      {exportError && (
        <Alert variant="destructive"><AlertDescription>{exportError}</AlertDescription></Alert>
      )}

      <QueryResultsTable response={query.data ?? null} requestedColumns={selectedColumns} pageSize={limit} />

      {query.data && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" data-testid="page-prev"
            disabled={offset <= 0 || query.isPending} onClick={() => run(Math.max(0, offset - limit))}>Previous</Button>
          <span className="text-sm text-muted-foreground">offset {offset}</span>
          <Button variant="outline" size="sm" data-testid="page-next"
            disabled={offset + returned >= total || query.isPending} onClick={() => run(offset + limit)}>Next</Button>
        </div>
      )}
    </div>
  );
}
